import React from "react";
import { Stack, Group, Text, Badge } from "@mantine/core";

const PAY_TYPE_LABELS = {
  FIXED_AMOUNT_PER_ACCOUNT: "Fixed Amount Per Account",
  SCALE_AMOUNT_PER_ACCOUNT: "Scaled Amount Per Account",
  SCALE_AMOUNT_TOTAL: "Scaled Amount Total",
  FIXED_PERCENT_ACV: "Fixed Percentage of ACV",
  SCALE_PERCENT_ACV: "Scaled Percentage of ACV",
  INITIAL_PRICE: "Initial Price",
};

const formatCurrency = value =>
  value == null
    ? "N/A"
    : `$${Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const formatPercent = value => (value == null ? "N/A" : `${value}%`);

// Read-only, document-style display of an upfront pay option. document_text is
// shown unless the caller renders it (e.g. at the section heading level).
function UpfrontPayDisplay({ item, hideDocumentText = false }) {
  if (!item) return null;

  const typeValue = item.upfront_pay_type;
  const scaleLines = Array.isArray(item.scale_lines) ? item.scale_lines : [];
  const isPercentScale = typeValue === "SCALE_PERCENT_ACV";
  // document_text may be a related object ({ document_text, ... }) or a string.
  const documentText =
    item.document_text && typeof item.document_text === "object"
      ? item.document_text.document_text
      : item.document_text;

  return (
    <Stack gap="xs">
      <Group gap="xs" align="center">
        <Text size="sm" fw={600}>
          {item.name}
        </Text>
        {item.is_default && (
          <Badge color="green" variant="light">
            Default
          </Badge>
        )}
      </Group>

      <Text size="sm" c="dimmed">
        {PAY_TYPE_LABELS[typeValue] ?? typeValue}
      </Text>

      {typeValue === "FIXED_AMOUNT_PER_ACCOUNT" && (
        <Group gap="xs">
          <Text size="sm" c="dimmed">
            Fixed Amount:
          </Text>
          <Text size="sm">{formatCurrency(item.fixed_amount)}</Text>
        </Group>
      )}

      {typeValue === "FIXED_PERCENT_ACV" && (
        <Group gap="xs">
          <Text size="sm" c="dimmed">
            Fixed Percentage:
          </Text>
          <Text size="sm">{formatPercent(item.fixed_percentage)}</Text>
        </Group>
      )}

      {scaleLines.length > 0 && (
        <Stack gap={4}>
          <Group gap="xs" wrap="nowrap">
            <Text size="xs" c="dimmed" style={{ flex: 1 }}>
              Account Number
            </Text>
            <Text size="xs" c="dimmed" style={{ flex: 1 }}>
              {isPercentScale ? "Percentage of ACV" : "Amount"}
            </Text>
          </Group>
          {scaleLines.map((line, index) => (
            <Group key={index} gap="xs" wrap="nowrap">
              <Text size="sm" style={{ flex: 1 }}>
                {line.accounts_amount}
              </Text>
              <Text size="sm" style={{ flex: 1 }}>
                {isPercentScale
                  ? formatPercent(line.percentage)
                  : formatCurrency(line.amount)}
              </Text>
            </Group>
          ))}
        </Stack>
      )}

      {!hideDocumentText && documentText && (
        <Text size="sm" c="#444" style={{ whiteSpace: "pre-wrap" }}>
          {documentText}
        </Text>
      )}
    </Stack>
  );
}

export default UpfrontPayDisplay;
